import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { BaseModel } from './base-model';
import { Company } from './company';
import { CompanyComplianceType } from './company-compliance-type';
import { TradeSpecificCompliance } from './trade-specific-compliance';

export class ComplianceSetting extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ComplianceSettingProperties: (
    | {
        property: keyof ComplianceSetting;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ComplianceSetting;
        type: 'alias';
      }
    | {
        property: keyof ComplianceSetting;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'riskLevel', type: 'column', dataType: 'number' },
    { property: 'CompanyId', type: 'column', dataType: 'number' },
    { property: 'companyIdentity', type: 'column', dataType: 'number' },
    { property: 'companyWatchlist', type: 'column', dataType: 'number' },
    { property: 'companyInGoodStanding', type: 'column', dataType: 'boolean' },
    { property: 'bankruptcy', type: 'column', dataType: 'number' },
    { property: 'companyLiens', type: 'column', dataType: 'number' },
    { property: 'isW9Checked', type: 'column', dataType: 'boolean' },
    { property: 'ownerIdentity', type: 'column', dataType: 'number' },
    { property: 'ownerWatchlist', type: 'column', dataType: 'number' },
    { property: 'ownerFelony', type: 'column', dataType: 'number' },
    { property: 'ownerMisdemeanor', type: 'column', dataType: 'number' },
    { property: 'ownerBankruptcy', type: 'column', dataType: 'number' },
    { property: 'ownerLiens', type: 'column', dataType: 'number' },
    { property: 'ownerSexOffender', type: 'column', dataType: 'boolean' },
    { property: 'employeeMisdemeanor', type: 'column', dataType: 'number' },
    { property: 'employeeFelony', type: 'column', dataType: 'number' },
    { property: 'employeeSexOffender', type: 'column', dataType: 'boolean' },
    {
      property: 'generalLiability_isRequired',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_isRequired',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'umbrellaLiability_isRequired',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'workersCompensation_isRequired',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'errorsAndOmissions_isRequired',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'generalLiability_policyAmountPerOccurance',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_policyAmountAggregate',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_productOps',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_medicalExpenses',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_damageToRentedPremises',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_hasAiOngoingOps',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_hasAiCompOps',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_hasPnc',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_hasFullPolicyDoc',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_nonOwnedAutoAmount',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_hasRequiredForms',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_contractualLiability',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_isPerOccurrence',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'generalLiability_financialSizeCategory',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'generalLiability_legalNameMismatch',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'personalAdvertisementInjury',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_hiredAutos',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'generalLiability_nonOwnedAutos',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'autoLiability_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'autoLiability_combinedSingleLimit',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'autoLiability_propertyDamageLimit',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'autoLiability_bodilyInjuryPerPerson',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'autoLiability_bodilyInjuryPerAccident',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'autoLiability_hasNonOwnedAuto',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_hasFullPolicyDoc',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_hasRequiredForms',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_financialSizeCategory',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'autoLiability_legalNameMismatch',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'allowNonCommercialPolicy',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'excessLiability_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'excessLiability_policyAmountPerOccurance',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'excessLiability_policyAmountAggregate',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'umbrellaLiability_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'umbrellaLiability_policyAmountPerOccurance',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'umbrellaLiability_policyAmountAggregate',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'umbrellaLiability_hasFullPolicyDoc',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'umbrellaLiability_hasRequiredForms',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'umbrellaLiability_financialSizeCategory',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'umbrellaLiability_legalNameMismatch',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'workersCompensation_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'workersCompensation_eachIncidentEmployersLimit',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'workersCompensation_eachEmployeeMinimum',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'workersCompensation_eachEmployerMinimum',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'workersCompensation_hasRequiredForms',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'workersCompensation_officerExclusionNotAllowed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'workersCompensation_financialSizeCategory',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'workersCompensation_legalNameMismatch',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'errorsAndOmissions_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'errorsAndOmissions_policyAmount',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'errorsAndOmissions_financialSizeCategory',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'professionalLiability_legalNameMismatch',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'tradeSpecificCoverage_amBestRating',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'tradeSpecificCoverage_autoOnHookCargoMinimumCoverage',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'tradeSpecificCoverage_autoGarageKeepersMinimumCoverage',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'tradeSpecificCoverage_GLPollutionCoverage',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'tradeSpecificCoverage_CrimeDishonestyFidelityBondCoverage',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'tradeSpecificCoverage_financialSizeCategory',
      type: 'column',
      dataType: 'string'
    },
    {
      property: 'generalLiability_InsNameFailed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_InsNameFailed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'umbrellaLiability_InsNameFailed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'workersCompensation_InsNameFailed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'professionalLiability_InsNameFailed',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'glAdditionalInsuredEndorsements',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoAdditionalInsuredEndorsements',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'siteType', type: 'column', dataType: 'string' },
    { property: 'glIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'autoIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'umbIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'wkIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'errIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'tradeIsNamed', type: 'column', dataType: 'boolean' },
    { property: 'glDescOps', type: 'column', dataType: 'boolean' },
    { property: 'autoDescOps', type: 'column', dataType: 'boolean' },
    { property: 'umbDescOps', type: 'column', dataType: 'boolean' },
    { property: 'wkDescOps', type: 'column', dataType: 'boolean' },
    { property: 'errDescOps', type: 'column', dataType: 'boolean' },
    { property: 'tradeDescOps', type: 'column', dataType: 'boolean' },
    { property: 'glIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'autoIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'umbIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'wkIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'errIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'tradeIsAgency', type: 'column', dataType: 'boolean' },
    { property: 'sampleCert', type: 'column', dataType: 'string' },
    { property: 'disallowWCExemptions', type: 'column', dataType: 'boolean' },
    {
      property: 'generalLiability_perProjLocAgg',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'umbrellaLiability_primaryNonContributory',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'umbrellaLiability_occurrenceBased',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_contingentAuto',
      type: 'column',
      dataType: 'boolean'
    },
    {
      property: 'autoLiability_trailerInterchange',
      type: 'column',
      dataType: 'number'
    },
    {
      property: 'autoLiability_cargoLiability',
      type: 'column',
      dataType: 'number'
    },
    { property: 'complianceSettingType', type: 'column', dataType: 'string' },
    { property: 'CompanyComplianceTypeId', type: 'column', dataType: 'number' },

    { property: 'Company', model: 'Company', type: 'association' },
    {
      property: 'CompanyComplianceType',
      model: 'CompanyComplianceType',
      type: 'association'
    },
    {
      property: 'TradeSpecificCompliances',
      model: 'TradeSpecificCompliance',
      type: 'association'
    }
  ];

  static {
    ComplianceSetting.ComplianceSettingProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        ComplianceSetting.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        ComplianceSetting.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        ComplianceSetting.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(ComplianceSettingObject: Partial<ComplianceSetting>) {
    super();

    ComplianceSetting.ComplianceSettingProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = ComplianceSettingObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  riskLevel?: number;
  CompanyId?: number;

  companyIdentity?: number | null;
  companyWatchlist?: number | null;
  companyInGoodStanding?: boolean | null;
  bankruptcy?: number | null;
  companyLiens?: number | null;
  isW9Checked?: boolean | null;

  ownerIdentity?: null | 0 | 10 | 20 | 30 | 40 | 50;
  ownerWatchlist?: null | 0 | 1 | 2;
  ownerFelony?: null | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  ownerMisdemeanor?: null | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  ownerBankruptcy?: null | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
  ownerLiens?: null | number;
  ownerSexOffender?: null | boolean;
  employeeMisdemeanor?: number;
  employeeFelony?: number;
  employeeSexOffender?: boolean;

  generalLiability_isRequired?: boolean;
  autoLiability_isRequired?: boolean;
  umbrellaLiability_isRequired?: boolean;
  workersCompensation_isRequired?: boolean;
  errorsAndOmissions_isRequired?: boolean;

  generalLiability_amBestRating?: string;
  generalLiability_policyAmountPerOccurance?: number;
  generalLiability_policyAmountAggregate?: number;
  generalLiability_productOps?: number;
  generalLiability_medicalExpenses?: number;
  generalLiability_damageToRentedPremises?: number;
  generalLiability_hasAiOngoingOps?: boolean;
  generalLiability_hasAiCompOps?: boolean;
  generalLiability_hasPnc?: boolean;
  generalLiability_hasFullPolicyDoc?: boolean;
  generalLiability_nonOwnedAutoAmount?: number;
  generalLiability_hasRequiredForms?: boolean;
  generalLiability_contractualLiability?: boolean;
  generalLiability_isPerOccurrence?: boolean;
  generalLiability_financialSizeCategory?: string | null;
  personalAdvertisementInjury?: number;
  generalLiability_hiredAutos?: number;
  generalLiability_nonOwnedAutos?: number;
  generalLiability_legalNameMismatch?: boolean;

  autoLiability_amBestRating?: string;
  autoLiability_combinedSingleLimit?: number;
  autoLiability_propertyDamageLimit?: number;
  autoLiability_bodilyInjuryPerPerson?: number;
  autoLiability_bodilyInjuryPerAccident?: number;
  autoLiability_hasNonOwnedAuto?: boolean;
  autoLiability_hasFullPolicyDoc?: boolean;
  autoLiability_hasRequiredForms?: boolean;
  autoLiability_financialSizeCategory?: string | null;
  allowNonCommercialPolicy?: boolean;
  autoLiability_contingentAuto?: boolean;
  autoLiability_trailerInterchange?: number | null;
  autoLiability_cargoLiability?: number | null;
  autoLiability_legalNameMismatch?: boolean;

  excessLiability_amBestRating?: string | null;
  excessLiability_policyAmountPerOccurance?: number;
  excessLiability_policyAmountAggregate?: number;

  umbrellaLiability_amBestRating?: string | null;
  umbrellaLiability_policyAmountPerOccurance?: number;
  umbrellaLiability_policyAmountAggregate?: number;
  umbrellaLiability_hasFullPolicyDoc?: boolean;
  umbrellaLiability_hasRequiredForms?: boolean;
  umbrellaLiability_financialSizeCategory?: string | null;
  umbrellaLiability_legalNameMismatch?: boolean;

  workersCompensation_amBestRating?: string | null;
  workersCompensation_eachIncidentEmployersLimit?: number;
  workersCompensation_eachEmployeeMinimum?: number;
  workersCompensation_eachEmployerMinimum?: number;
  workersCompensation_hasRequiredForms?: boolean;
  workersCompensation_officerExclusionNotAllowed?: boolean;
  workersCompensation_financialSizeCategory?: string | null;
  workersCompensation_legalNameMismatch?: boolean;

  errorsAndOmissions_amBestRating?: string | null;
  errorsAndOmissions_policyAmount?: number;
  errorsAndOmissions_financialSizeCategory?: string | null;
  professionalLiability_legalNameMismatch?: boolean;

  tradeSpecificCoverage_amBestRating?: string | null;
  tradeSpecificCoverage_autoOnHookCargoMinimumCoverage?: number;
  tradeSpecificCoverage_autoGarageKeepersMinimumCoverage?: number;
  tradeSpecificCoverage_GLPollutionCoverage?: number;
  tradeSpecificCoverage_CrimeDishonestyFidelityBondCoverage?: number;
  tradeSpecificCoverage_financialSizeCategory?: string | null;

  glAdditionalInsuredEndorsements?: boolean;
  autoAdditionalInsuredEndorsements?: boolean;

  generalLiability_InsNameFailed?: boolean;
  autoLiability_InsNameFailed?: boolean;
  umbrellaLiability_InsNameFailed?: boolean;
  workersCompensation_InsNameFailed?: boolean;
  professionalLiability_InsNameFailed?: boolean;

  siteType?: 'on' | 'off' | 'global';

  glIsNamed?: boolean;
  autoIsNamed?: boolean;
  umbIsNamed?: boolean;
  wkIsNamed?: boolean;
  errIsNamed?: boolean;
  tradeIsNamed?: boolean;
  glDescOps?: boolean;
  autoDescOps?: boolean;
  umbDescOps?: boolean;
  wkDescOps?: boolean;
  errDescOps?: boolean;
  tradeDescOps?: boolean;
  glIsAgency?: boolean;
  autoIsAgency?: boolean;
  umbIsAgency?: boolean;
  wkIsAgency?: boolean;
  errIsAgency?: boolean;
  tradeIsAgency?: boolean;

  sampleCert?: string | null;
  disallowWCExemptions?: boolean;

  generalLiability_perProjLocAgg?: boolean;
  umbrellaLiability_primaryNonContributory?: boolean;
  umbrellaLiability_occurrenceBased?: boolean;
  complianceSettingType?: string;
  CompanyComplianceTypeId?: number;

  Company?: Company;
  CompanyComplianceType?: CompanyComplianceType;
  TradeSpecificCompliances?: TradeSpecificCompliance[];
}
